import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, AlertTriangle, CheckCircle2, Info } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

const iconFor = (type) => {
  if (type === "DECISION_DUE") return <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />;
  if (type === "LETTER_NEW") return <Info className="h-3.5 w-3.5 text-sky-600" />;
  if (type === "IURAN_UNPAID") return <AlertTriangle className="h-3.5 w-3.5 text-rose-600" />;
  if (type === "ANNOUNCEMENT") return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />;
  return <Info className="h-3.5 w-3.5 text-muted-foreground" />;
};

export function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const load = () => {
    api.get("/notifications").then((r) => setItems(r.data.items || [])).catch(() => {});
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60000); // Refresh every minute
    return () => clearInterval(id);
  }, []);

  const highCount = items.filter((i) => i.priority === "HIGH").length;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative rounded-full" data-testid="btn-notification-bell">
          <Bell className="h-5 w-5" />
          {items.length > 0 && (
            <span
              className={`absolute -top-0.5 -right-0.5 min-w-4 h-4 rounded-full text-[0.6rem] font-bold flex items-center justify-center px-1 ${
                highCount > 0 ? "bg-rose-600 text-white" : "bg-secondary text-secondary-foreground"
              }`}
              data-testid="notification-count"
            >
              {items.length > 9 ? "9+" : items.length}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifikasi</span>
          <span className="text-[0.7rem] font-normal text-muted-foreground">{items.length} item</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.length === 0 ? (
          <div className="py-8 text-center text-xs text-muted-foreground">
            <CheckCircle2 className="h-6 w-6 mx-auto mb-2 text-emerald-500" />
            Tidak ada notifikasi
          </div>
        ) : (
          items.map((it) => (
            <Link
              key={it.id}
              to={it.link}
              className="block px-3 py-2.5 hover:bg-muted/70 border-b border-border/50 last:border-0"
              onClick={() => setOpen(false)}
              data-testid={`notification-${it.id}`}
            >
              <div className="flex items-start gap-2">
                <div className="mt-0.5">{iconFor(it.type)}</div>
                <div className="flex-1 min-w-0">
                  <div className={`text-xs font-semibold leading-tight ${it.priority === "HIGH" ? "text-rose-700" : "text-foreground"}`}>{it.title}</div>
                  {it.subtitle && <div className="text-[0.7rem] text-muted-foreground mt-0.5 truncate">{it.subtitle}</div>}
                </div>
              </div>
            </Link>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
